"use client"

import type React from "react"
import { useState, useEffect } from "react"
import api from "../../libs/axios"
import toast from "react-hot-toast"
import { FiX, FiKey, FiUsers, FiCalendar, FiCheck, FiLoader } from "react-icons/fi"
import Loader from "../../utils/loader"

interface ViewRoleOffcanvasProps {
    roleId: string
    showViewRoleCanvas: boolean
    setShowViewRoleCanvas: (show: boolean) => void
}

export interface IPermission {
    id: number
    name: string
    description: string
    resource: string
    action: string
    RolePermission?: {
        id: number
        role_id: number
        permission_id: number
        created_at: string
        updated_at: string
    }
}

export interface IRole {
    id: number
    name: string
    description: string
    is_active: boolean
    created_at: string
    updated_at: string
    permissions?: IPermission[]
    users_count?: number
}

const ViewRoleOffcanvas: React.FC<ViewRoleOffcanvasProps> = ({ 
    roleId, 
    showViewRoleCanvas, 
    setShowViewRoleCanvas 
}) => {
    const [role, setRole] = useState<IRole | null>(null)
    const [loading, setLoading] = useState(false)

    // Get module icon
    const getModuleIcon = (resource: string) => {
        switch (resource) {
            case "tournament":
                return <FiCalendar className="w-4 h-4" />
            case "match":
                return <FiCheck className="w-4 h-4" />
            case "team":
                return <FiUsers className="w-4 h-4" />
            case "ground":
                return <FiCalendar className="w-4 h-4" />
            case "user":
                return <FiUsers className="w-4 h-4" />
            default:
                return <FiKey className="w-4 h-4" />
        }
    }

    // Get status style
    const getStatusStyle = (is_active: boolean | undefined) => {
        if (is_active) {
            return "bg-green-100 text-green-800"
        } else {
            return "bg-red-100 text-red-800"
        }
    }

    // Fetch role data
    const fetchRoleData = async () => {
        if (!roleId) return
        
        try {
            setLoading(true)
            const response = await api.get(`/roles/${roleId}`)
            setRole(response.data.role)
        } catch (error: any) {
            console.error("Error fetching role:", error)
            toast.error("Failed to fetch role data")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (roleId && showViewRoleCanvas) {
            fetchRoleData()
        }
    }, [roleId, showViewRoleCanvas])

    // Group permissions by resource
    const permissionsByResource =
        role?.permissions?.reduce(
            (acc, permission) => {
                if (!acc[permission.resource]) {
                    acc[permission.resource] = []
                }
                acc[permission.resource].push(permission)
                return acc
            },
            {} as Record<string, IPermission[]>,
        ) || {}

    const handleClose = () => {
        setShowViewRoleCanvas(false)
        setRole(null)
    }

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
                    showViewRoleCanvas ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
                    showViewRoleCanvas ? "translate-x-0" : "translate-x-full"
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiKey className="mr-2" />
                        Role Details
                    </h2>
                    <button 
                        onClick={handleClose} 
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <FiLoader className="w-8 h-8 animate-spin text-primary-600" />
                    </div>
                ) : role ? (
                    <div className="flex flex-col h-full">
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Role Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                                        <div className="flex items-center">
                                            <FiKey className="w-4 h-4 mr-2 text-primary-600" />
                                            <span className="text-lg font-semibold text-gray-900">{role.name}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <span className={`px-3 py-1 capitalize text-sm rounded-full ${getStatusStyle(role.is_active)}`}>
                                            {role.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Users Assigned</label>
                                        <div className="flex items-center">
                                            <FiUsers className="w-4 h-4 mr-2 text-gray-500" />
                                            <span className="text-lg font-medium text-gray-900">{role.users_count || 0}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Created On</label>
                                        <div className="flex items-center">
                                            <FiCalendar className="w-4 h-4 mr-2 text-gray-500" />
                                            <span className="text-gray-900">
                                                {role.created_at ? new Date(role.created_at).toLocaleDateString() : "N/A"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {role.description && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <p className="text-gray-900 bg-gray-50 p-3 rounded-md">{role.description}</p>
                                    </div>
                                )}
                            </div>

                            {/* Permissions */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                                    <FiKey className="inline mr-1" />
                                    Permissions ({role.permissions?.length || 0})
                                </h3>

                                {Object.keys(permissionsByResource).length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">No permissions assigned to this role</div>
                                ) : (
                                    Object.entries(permissionsByResource).map(([resource, permissions]) => (
                                        <div key={resource} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-center mb-3">
                                                {getModuleIcon(resource)}
                                                <h4 className="text-md font-medium text-gray-900 ml-2 capitalize">{resource}</h4>
                                                <span className="ml-2 text-sm text-gray-500">({permissions.length} permissions)</span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {permissions.map((permission) => (
                                                    <div key={permission.id} className="flex items-start">
                                                        <FiCheck className="w-4 h-4 mt-1 mr-2 text-green-600" />
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{permission.name}</div>
                                                            <div className="text-xs text-gray-500">{permission.description}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 p-6">
                            <div className="flex space-x-3">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-64">
                        <p className="text-gray-500">No role data available</p>
                    </div>
                )}
            </div>
        </>
    )
}

export default ViewRoleOffcanvas